import {IController, IModule, IOnChangesObject, IScope} from "angular";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {WebNavigasjon} from "../../../../tjenester/WebNavigasjon";
import {Debugger} from "debug";
import {changesInneholder} from "felles/stoettefunk/angularHjelpemetoder";
import {Logger} from "felles/infrastruktur/Logging";
import {LokaltDatalager} from "felles/tjenester/LokaltDatalager";
import _ from "lodash";
import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {MeldingTilBrukerVedInnlogging} from "../../../../tjenester/MeldingTilBrukerVedInnlogging";
import {MeldingTilBruker, Varighet} from "felles/tjenester/MeldingTilBruker";
import {SystemmeldingRessurs} from "felles/tjenester/ressurser/SystemmeldingRessurs";
import {UIRouterGlobals} from "@uirouter/angularjs";
import {gettextCatalog} from "angular-gettext";

const css = require('./autentisering.scss');
const debug:Debugger = require('debug')('AutentiseringKontroller');

/**
 * Viser GUI for autentisering.
 * <pre>
 * Bruker kan velge mellom ulike autentiseringsmekanismer.
 *
 * MERK at dersom bruker velger å autentisere med e-id (criipto) MÅ criiptoCallbackparams angis.
 * MERK at dersom klient har behov for å propagere tilstand til {@link #vedAutentisert} callbacket må denne 
 * tilstand propageres via feks cookie eller sessions storage da siden vil bli relastet ifbm criipto-autentisering
 *
 *
 * NB! Dersom man velger {@link #criiptoGuiModus} = {@link l.CriiptoGuiModus#popup} så kan {@link #vedAutentisert} med tilstand brukes.
 *
 * </pre>
 */
export class AutentiseringKontroller implements IController {

    private static forbliInnloggetNøkkel = 'forbliInnlogget';

    static registrerKomponent(angular: IModule) {
        angular
            .component('wmAutentisering', {
                bindings: {
                    criiptoCallbackparams: '<',
                    vedAutentisert: '&',
                    criiptoGuiModus: '<?'
                },
                controller: AutentiseringKontroller.forInjisering(),
                template: require('./autentisering.html')
            });
    }

    static forInjisering() {
        return [
            regNavn.ng.$scope,
            regNavn.ng.ui.$uiRouterGlobals,
            SystemmeldingRessurs.regNavn,
            MeldingTilBruker.regNavn,
            WebNavigasjon.regNavn,
            Logger.regNavn,
            LokaltDatalager.regNavn,
            regNavn.gettext.gettextCatalog,   AutentiseringKontroller
        ]
    }

    constructor(private $scope: IScope,
                private $uiRouterGlobals: UIRouterGlobals,
                private systemmeldingRessurs: SystemmeldingRessurs,
                private meldingTilBruker: MeldingTilBruker,
                private navigasjon: WebNavigasjon,
                private logger: Logger,
                private datalager: LokaltDatalager,
                private gettextCatalog: gettextCatalog) {
    }

    criiptoCallbackparams:l.CriiptoCallbackParams; //satt via bindings
    vedAutentisert: (args: { autentisert: l.AutentisertOkModell }) => void; //satt via bindings

    visHuskMegValg = true;
    visKunCriiptodialog = false;
    visKunPassorddialog = false;
    forbliInnlogget = false;
    criiptoGuiModus : l.CriiptoGuiModus = l.CriiptoGuiModus.erstatt //satt via bindings
    bankidSystemmelding: l.SystemMeldingModell = null;
    stoettedeMetoderMelding: string = null;

    tokenvarighet(){
        return this.forbliInnlogget ? l.Tokenvarighet.medium : l.Tokenvarighet.kort;
    }

    autentisert(autentisert:l.AutentisertOkModell){
        this.vedAutentisert({autentisert: autentisert});
    }

    brukerPassordValgt(aktivert:boolean){
        this.visKunPassorddialog = aktivert;
    }

    kreverOppgraderingTil(autentiseringsmetode:l.Autentiseringsmetode){
        this.stoettedeMetoderMelding = this.gettextCatalog.getString('Kun følgende innloggingsmetoder er aktivert på din konto:')
        if (autentiseringsmetode == l.Autentiseringsmetode.criiptoOpenId){
            this.visKunCriiptodialog = true;
        }
    }

    eidValgt(){
        //vi skjuler passorddialog når criiptoCallbackparams er satt - se $onChanges
    }

    vedCriiptoBrukerIkkeFunnet(ukjentSubject: l.SubjectIdIkkeKnyttetTilBrukerModell){
        this.navigasjon.gåTilBindBrukerOpenId(ukjentSubject);
    }

    vedCriiptoAvbrutt(){
        this.navigasjon.gåTilInnloggingssiden('', true);
    }

    vedCriiptoFeil(feil:any){
        debug("criiptoFeil", feil)
        this.logger.error(feil)
        this.navigasjon.gåTilInnloggingssiden('', true);
    }

    // dette må lagres i local storage så det er tilgjengelig når cripto callback returerer. tilstand må huskes.
    vedEndringHuskI30Dager(){
        this.datalager.lagre(AutentiseringKontroller.forbliInnloggetNøkkel, this.forbliInnlogget)
    }

    $onInit(): void {
        this.initHuskMeg();
        this.lastSystemmeldinger();
    }

    $onChanges(changes: IOnChangesObject): void {
        if (changesInneholder(changes, 'criiptoCallbackparams'))
            this.visKunCriiptodialog = true;
    }

    //hjelpemetoder

    private initHuskMeg(){
        if (!_.isNil(this.$uiRouterGlobals.params["huskmeg"])) {
            this.aktiverHuskeMegValg()
        } else
            this.datalager.hentEllerStandardVerdi(AutentiseringKontroller.forbliInnloggetNøkkel, false)
                .then(forbliInnlogget => this.forbliInnlogget = forbliInnlogget);

        //dersom bruker klikker på "oppgader til medium sesjon"-melding etter at brukers sesjon har timet ut vil denne eventet fyres
        this.$scope.$on(MeldingTilBrukerVedInnlogging.eventer.klikkPåOppgraderTilMediumTokenvarighetNåLoggetUt, ()=>this.aktiverHuskeMegValg());
    }

    private aktiverHuskeMegValg() {
        this.visHuskMegValg = true;
        this.forbliInnlogget = true;
    }

    private lastSystemmeldinger(){
        this.systemmeldingRessurs.hentMedType([l.Meldingstype.loggInnHoved, l.Meldingstype.loggInnBankId]).$promise
            .then(meldinger => this.håndterSystemmeldinger(meldinger));
    }

    private håndterSystemmeldinger(meldinger: l.SystemMeldingModell[]) {
        meldinger.forEach(melding =>{
            switch (melding.meldingstype){
                case l.Meldingstype.loggInnHoved:
                    switch (melding.varseltype){
                        case l.Varseltype.info:
                            return this.meldingTilBruker.infomelding(melding.melding, Varighet.sideskift);
                        case l.Varseltype.advarsel:
                            return this.meldingTilBruker.advarselmelding(melding.melding, Varighet.sideskift);
                        case l.Varseltype.feil:
                            return this.meldingTilBruker.feilmelding(melding.melding, Varighet.sideskift);
                    }
                    break;

                case l.Meldingstype.loggInnBankId:
                    this.bankidSystemmelding = melding;
                    break;
            }
        })
    }

}
